import React from 'react';
import { Box, TextField, InputAdornment, Typography, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RowThumb from './RowThumb';
import { radius } from '../../themes/smartinventoryTheme';

// Searchable single-select card list — shared by Add inventory and Custom
// invoice. `products`: [{ id, name, description, price, unit, disabled }].
// `price` is already formatted (currency) by the caller. `disabled` greys out
// and locks a row (e.g. out of stock) while keeping it visible, so admins can
// see it exists without being able to pick it.
const ProductPicker = ({ products, value, onChange, searchValue, onSearchChange, searchPlaceholder, emptyLabel }) => (
  <Box>
    <TextField
      fullWidth
      value={searchValue}
      onChange={onSearchChange}
      placeholder={searchPlaceholder}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
      sx={{ mb: 1.5 }}
    />
    <Box role="listbox" sx={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
      {products.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          {emptyLabel}
        </Typography>
      )}
      {products.map((product) => {
        const selected = product.id === value;
        const disabled = !!product.disabled;
        return (
          <Box
            key={product.id}
            role="option"
            aria-selected={selected}
            aria-disabled={disabled}
            onClick={disabled ? undefined : () => onChange(product.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.25,
              border: '1px solid',
              borderColor: selected ? 'primary.main' : 'divider',
              borderRadius: `${radius.md}px`,
              bgcolor: selected ? 'primary.light' : 'transparent',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
            }}
          >
            <RowThumb src={product.image} name={product.name} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" noWrap>{product.name}</Typography>
              {product.description && (
                <Typography variant="body2" color="text.secondary" noWrap>{product.description}</Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Typography variant="subtitle1">{product.price}</Typography>
              {product.unit && <Chip label={product.unit} size="small" variant="outlined" />}
            </Box>
          </Box>
        );
      })}
    </Box>
  </Box>
);

export default ProductPicker;
